import { useMemo } from 'react';
import type { NavbarVariant } from '@packages/ui/layout';
import { useGetCurrentTenantInfo, useGetSandboxSwitchable } from '@/hooks';
import useInstance from '@/hooks/custom-hooks/useInstance';
import usePermissionCheck from '@/hooks/custom-hooks/usePermissionCheck';

export interface TenantInfoResult {
  tenantName: string | null;
  tenantType: string | null;
  companyId: string | null;
  isSandbox: boolean;
  isSwitched: boolean;
  instanceName: string | null;
  variant: NavbarVariant;
  canSwitchCompany: boolean;
  canSwitchSandbox: boolean;
  canSwitch: boolean;
  isLoading: boolean;
}

interface UseTenantInfoOptions {
  handleCustomerRequest: <T>(request: Promise<T>) => Promise<T | undefined>;
}

/**
 * Customer-only hook that encapsulates tenant/sandbox/instance state.
 * Derives the navbar variant and switch permissions.
 *
 * Must be rendered inside `<AuthGate>` — no auth guard needed.
 */
export function useTenantInfo({
  handleCustomerRequest,
}: UseTenantInfoOptions): TenantInfoResult {
  const { data: currentTenant, isLoading: isTenantLoading } =
    useGetCurrentTenantInfo(handleCustomerRequest);

  const { data: switchableOptions } = useGetSandboxSwitchable(
    handleCustomerRequest,
    undefined,
    Boolean(currentTenant)
  );

  const { isSwitched, getInstance } = useInstance();

  const switched = Boolean(isSwitched());
  const instanceInfo = switched ? getInstance() : { instanceName: null, instanceId: null };
  const isSandbox = switched && instanceInfo?.instanceName !== 'Main';

  const hasSwitchCompanyPermission = usePermissionCheck({
    type: 'View',
    feature: 'Customer',
    permission: 'SwitchCompany',
  });

  const hasSandboxPermission = usePermissionCheck({
    type: 'View',
    feature: 'Customer',
    permission: 'Sandbox',
  });

  const resolvedInstanceName = useMemo(() => {
    if (switched && instanceInfo?.instanceName) return instanceInfo.instanceName;
    const tenantId = currentTenant?.id ?? '';
    return switchableOptions?.find((item) => item.value === tenantId)?.label ?? null;
  }, [switched, instanceInfo?.instanceName, currentTenant?.id, switchableOptions]);

  const variant = useMemo((): NavbarVariant => {
    if (isSandbox) return 'sandbox';
    if (switched) return 'switched';
    return 'default';
  }, [isSandbox, switched]);

  return {
    tenantName: currentTenant?.companyName ?? null,
    tenantType: currentTenant?.tenantType ?? null,
    companyId: currentTenant?.companyId ?? null,
    isSandbox,
    isSwitched: switched,
    instanceName: resolvedInstanceName,
    variant,
    canSwitchCompany: hasSwitchCompanyPermission,
    canSwitchSandbox: hasSandboxPermission,
    canSwitch: hasSwitchCompanyPermission || hasSandboxPermission,
    isLoading: isTenantLoading,
  };
}
